import React, { Component, isValidElement } from 'react'
import ReactDOM from 'react-dom'
import sizeMe from 'react-sizeme'
import shallowequal from 'shallowequal'

import GridItem from './GridItem'

const isNumber = v => typeof v === 'number' && isFinite(v)
const isPercentageNumber = v => typeof v === 'string' && /^\d+(\.\d+)?%$/.test(v)

const createArray = (v, l) => {
  const array = []
  for (let i = 0; i < l; i += 1) array.push(v)
  return array
}

const getColumnLengthAndWidth = (width, value, gutter) => {
  if (isNumber(value)) {
    const columnWidth = parseFloat(value)

    return [Math.floor((width - (width / columnWidth - 1) * gutter) / columnWidth), columnWidth]
  } else if (isPercentageNumber(value)) {
    const columnPercentage = parseFloat(value) / 100
    const maxColumn = Math.floor(1 / columnPercentage)
    const columnWidth = (width - gutter * (maxColumn - 1)) / maxColumn

    return [maxColumn, columnWidth]
  } else {
    throw new Error('Should be columnWidth is a number or percentage string.')
  }
}

export class GridInline extends Component {
  constructor(props) {
    super(props)

    this.items = {}
    this.imgLoad = {}
    this.mounted = false
    this.state = this.doLayout(props)
  }

  componentDidMount() {
    this.mounted = true
    this.updateLayout(this.props)
  }

  // TODO warning dont do that
  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!shallowequal(nextProps, this.props)) {
      this.updateLayout(nextProps)
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return !shallowequal(nextProps, this.props) || !shallowequal(nextState, this.state)
  }

  componentWillUnmount() {
    this.mounted = false
    if (this.pendingLayoutFrame) {
      cancelAnimationFrame(this.pendingLayoutFrame)
      this.pendingLayoutFrame = null
    }
  }

  // Coalesce item-resize notifications into a single re-layout per frame, so a batch of cards
  // changing height at once (e.g. images loading) triggers one layout pass, not one per item.
  handleItemResize = () => {
    if (this.pendingLayoutFrame) return
    this.pendingLayoutFrame = requestAnimationFrame(() => {
      this.pendingLayoutFrame = null
      this.updateLayout(this.props)
    })
  }

  setStateIfNeeded(state) {
    if (this.mounted) {
      this.setState(state)
    }
  }

  getItemHeight(item) {
    // TODO warning dont do that
    // eslint-disable-next-line no-prototype-builtins
    if (item.key && this.items.hasOwnProperty(item.key)) {
      const component = this.items[item.key]
      // TODO warning dont do that
      // eslint-disable-next-line react/no-find-dom-node
      const el = ReactDOM.findDOMNode(component)
      const candidate = [el.scrollHeight, el.clientHeight, el.offsetHeight, 0].filter(isNumber)

      return Math.max(...candidate)
    }

    return 0
  }

  doLayout(props) {
    const results = this.doLayoutForClient(props)

    // eslint-disable-next-line react/destructuring-assignment
    if (this.mounted && typeof this.props.onLayout === 'function') {
      // eslint-disable-next-line react/destructuring-assignment
      this.props.onLayout()
    }

    return results
  }

  doLayoutForClient(props) {
    const {
      size: { width: containerWidth },
      columnWidth: rawColumnWidth,
      gutterWidth,
      gutterHeight,
      horizontal,
    } = props

    const childArray = React.Children.toArray(props.children)
    const [maxColumn, columnWidth] = getColumnLengthAndWidth(containerWidth, rawColumnWidth, gutterWidth)
    const columnHeights = createArray(0, maxColumn)

    let rects
    if (!horizontal) {
      rects = childArray.map(child => {
        const column = columnHeights.indexOf(Math.min(...columnHeights))
        const height = this.getItemHeight(child)
        const left = column * columnWidth + column * gutterWidth
        const top = columnHeights[column]

        columnHeights[column] += Math.round(height) + gutterHeight

        return { top, left, width: columnWidth, height }
      })
    } else {
      const sumHeights = childArray.reduce(
        (sum, child) => sum + Math.round(this.getItemHeight(child)) + gutterHeight,
        0,
      )
      const maxHeight = sumHeights / maxColumn

      let currentColumn = 0
      rects = childArray.map(child => {
        const column = currentColumn >= maxColumn - 1 ? maxColumn - 1 : currentColumn
        const height = this.getItemHeight(child)
        const left = column * columnWidth + column * gutterWidth
        const top = columnHeights[column]

        columnHeights[column] += Math.round(height) + gutterHeight
        if (columnHeights[column] >= maxHeight) {
          currentColumn += 1
        }

        return { top, left, width: columnWidth, height }
      })
    }

    const width = maxColumn * columnWidth + (maxColumn - 1) * gutterWidth
    const height = Math.max(...columnHeights) - gutterHeight
    const finalRects = rects.map(o => ({
      ...o,
      left: o.left + (containerWidth - width) / 2,
    }))

    return { rects: finalRects, actualWidth: width, height, columnWidth }
  }

  updateLayout(props) {
    if (!props) {
      this.setStateIfNeeded(this.doLayout(this.props))
    } else {
      this.setStateIfNeeded(this.doLayout(props))
    }
  }

  handleItemMounted = item => {
    const { itemKey: key } = item.props
    this.items[key] = item
    this.updateLayout(this.props)
  }

  handleItemUnmount = item => {
    const { itemKey: key } = item.props

    // TODO warning dont do that
    // eslint-disable-next-line no-prototype-builtins
    if (this.items.hasOwnProperty(key)) {
      delete this.items[key]
    }

    // TODO warning dont do that
    // eslint-disable-next-line no-prototype-builtins
    if (this.imgLoad.hasOwnProperty(key)) {
      this.imgLoad[key].off('always')
      delete this.imgLoad[key]
    }
  }

  handleRef = () => {
    this.props.refCallback(this)
  }

  render() {
    const {
      gutterWidth,
      gutterHeight,
      columnWidth: rawColumnWidth,
      enableSSR,
      onLayout,
      horizontal,
      rtl,
      refCallback,
      className,
      style,
      size,
      component,
      itemComponent,
      children,
      ...rest
    } = this.props

    const { rects, actualWidth, height } = this.state
    const containerSize = {
      actualWidth,
      width: size.width == null ? 0 : size.width,
      height,
    }
    const validChildren = React.Children.toArray(children).filter(child => isValidElement(child))

    return (
      <div
        className={className}
        style={{
          ...(style || {}),
          position: 'relative',
          height,
        }}
        ref={this.handleRef}
      >
        {validChildren.map((child, i) => (
          <GridItem
            {...rest}
            index={i}
            key={child.key}
            component={itemComponent}
            itemKey={child.key}
            rect={rects[i]}
            rtl={rtl}
            containerSize={containerSize}
            onMounted={this.handleItemMounted}
            onUnmount={this.handleItemUnmount}
            onResize={this.handleItemResize}
          >
            {child}
          </GridItem>
        ))}
      </div>
    )
  }
}

const SizeAwareGridInline = sizeMe({
  monitorWidth: true,
  monitorHeight: false,
})(GridInline)

const DEFAULT_PROPS = {
  style: {},
  gridRef: null,
  component: 'div',
  itemComponent: 'span',
  columnWidth: 150,
  gutterWidth: 5,
  gutterHeight: 5,
  units: { length: 'px', angle: 'deg' },
  vendorPrefix: true,
  userAgent: null,
  enableSSR: false,
  onLayout: null,
  horizontal: false,
  rtl: false,
  observeItemResize: false,
}

/**
 * This component is a fork of react-stack-grid v.0.7.1 (https://github.com/tsuyoshiwada/react-stack-grid)
 * - The code has been adapted to our lint/prettier rules
 * - All code related to the animations (and react-transition-group v1 - outdated) has been removed as it was
 * unused in our case and causing rendering issues
 */
export default class StackGrid extends Component {
  updateLayout() {
    this.grid.updateLayout()
  }

  handleRef = grid => {
    this.grid = grid

    if (typeof this.props.gridRef === 'function') {
      this.props.gridRef(this)
    }
  }

  render() {
    const props = { ...DEFAULT_PROPS, ...this.props }
    const { enableSSR, gridRef, ...rest } = props

    sizeMe.enableSSRBehaviour = enableSSR

    return <SizeAwareGridInline {...rest} refCallback={this.handleRef} />
  }
}
